import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import {
  aboutPhoto,
  contactLink,
  galleryItems,
  missingRequiredLinks,
  siteConfig,
  socialLinks,
} from "./data";
import { enableAnalytics, track } from "./analytics";
import type { AnalyticsEvent, LinkItem } from "./types";
import { localizedCopy, localeFromPath, type SiteLocale } from "./locales";

const CONSENT_KEY = "daniel-montana-analytics-consent";
const BIRTHDAY_DATE = "2026-09-02";

const isBirthdayToday = (date = new Date()) =>
  [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-") === BIRTHDAY_DATE;

@customElement("daniel-site")
export class DanielSite extends LitElement {
  @state() private consent: string | null = null;
  @state() private birthdayOpen = isBirthdayToday();
  @property({ type: Boolean }) private gallerySeen = false;
  @state() private revealedRestrictedId: string | undefined;
  @state() private gallery = galleryItems;
  private scrollMilestones = new Set<number>();
  private playedVideoIds = new Set<string>();
  private userInitiatedVideoIds = new Set<string>();
  private observer?: IntersectionObserver;
  private unsubscribeGallery?: () => void;
  private readonly locale: SiteLocale = localeFromPath();

  static styles = css`
    :host {
      --ink: #111;
      --paper: #f5f3ef;
      --muted: #aaa5a1;
      --wine: #8f2942;
      --wine-light: #b34b65;
      --line: rgba(245, 243, 239, 0.2);
      display: block;
      color: var(--paper);
      background: var(--ink);
      font-family: Arial, Helvetica, sans-serif;
    }
    * {
      box-sizing: border-box;
    }
    a {
      color: inherit;
    }
    button,
    a {
      -webkit-tap-highlight-color: transparent;
    }
    button:focus-visible,
    a:focus-visible {
      outline: 2px solid var(--paper);
      outline-offset: 4px;
    }
    .page {
      overflow: hidden;
    }
    .eyebrow {
      font-size: 0.68rem;
      letter-spacing: 0.19em;
      text-transform: uppercase;
      font-weight: 700;
      color: var(--muted);
      margin: 0 0 1rem;
    }
    .display {
      font-family: Georgia, "Times New Roman", serif;
      font-weight: 400;
      letter-spacing: -0.055em;
      line-height: 0.9;
    }
    .hero {
      min-height: 100svh;
      position: relative;
      display: flex;
      align-items: flex-end;
      padding: 1.5rem 1.25rem calc(2rem + env(safe-area-inset-bottom));
      isolation: isolate;
      background:
        radial-gradient(circle at 75% 34%, #793044 0 12%, transparent 40%),
        linear-gradient(130deg, #121212 20%, #24181b 60%, #574047);
    }
    .hero::after {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(0deg, rgba(0, 0, 0, 0.75), transparent 57%);
      z-index: -1;
    }
    .hero-art {
      position: absolute;
      inset: 0;
      z-index: -2;
      opacity: 0.95;
      background:
        linear-gradient(90deg, transparent 25%, rgba(0, 0, 0, 0.08)),
        url("/gallery/9F914B14-A508-4A8D-BAEF-A344AF7F932D.jpeg") center/cover;
    }
    .topmark {
      position: absolute;
      top: 1.5rem;
      left: 1.25rem;
      font: 700 0.76rem/1 Arial;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }
    .topmark span {
      display: block;
      font: 400 0.58rem/1.6 Arial;
      letter-spacing: 0.12em;
      color: #d6cdc6;
    }
    .language-switcher {
      position: absolute;
      top: 1.25rem;
      right: 1.25rem;
      z-index: 1;
      display: flex;
      align-items: center;
      font-size: 0.65rem;
      font-weight: bold;
      letter-spacing: 0.12em;
      background: rgba(9, 9, 9, 0.46);
      border: 1px solid rgba(245, 243, 239, 0.18);
      border-radius: 999px;
      backdrop-filter: blur(8px);
    }
    .language-switcher a {
      width: 44px;
      height: 44px;
      display: grid;
      place-items: center;
      color: #d6cdc6;
      text-decoration: none;
      border-bottom: 2px solid transparent;
    }
    .language-switcher a[aria-current="page"] {
      color: var(--paper);
      border-color: var(--paper);
    }
    .language-separator {
      color: rgba(245, 243, 239, 0.55);
      pointer-events: none;
    }
    .hero-content {
      max-width: 42rem;
    }
    h1 {
      font-size: clamp(4.2rem, 18vw, 9rem);
      margin: 0;
    }
    .descriptor {
      margin: 1rem 0 0.6rem;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      font-size: 0.71rem;
      font-weight: bold;
    }
    .intro {
      max-width: 25rem;
      margin: 0 0 1.75rem;
      color: #e0d8d3;
      line-height: 1.55;
    }
    .actions {
      display: grid;
      gap: 0.7rem;
      max-width: 26rem;
    }
    .button {
      min-height: 3.4rem;
      display: flex;
      justify-content: center;
      align-items: center;
      border: 1px solid var(--paper);
      padding: 0.8rem 1rem;
      text-decoration: none;
      text-transform: uppercase;
      font-size: 0.68rem;
      font-weight: bold;
      letter-spacing: 0.12em;
      transition:
        background 0.2s,
        color 0.2s,
        transform 0.2s;
      cursor: pointer;
    }
    .button-icon {
      width: 1.15rem;
      height: 1.15rem;
      flex: 0 0 auto;
      object-fit: contain;
      margin-right: 0.6rem;
    }
    .button:hover {
      transform: translateY(-2px);
    }
    .button.primary {
      background: var(--wine);
      border-color: var(--wine);
    }
    .button.primary:hover {
      background: var(--wine-light);
    }
    .button.secondary {
      background: rgba(17, 17, 17, 0.18);
    }
    .button.disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
    section {
      padding: 5.5rem 1.25rem;
    }
    .section-heading {
      margin-bottom: 2.5rem;
    }
    h2 {
      font-size: clamp(2.8rem, 11vw, 5.5rem);
      margin: 0;
    }
    .lead {
      max-width: 35rem;
      color: #605d5a;
      line-height: 1.65;
      margin: 1.1rem 0 0;
    }
    .gallery {
      color: #151313;
      background: var(--paper);
    }
    .masonry {
      columns: 2;
      gap: 0.7rem;
    }
    .gallery-card {
      break-inside: avoid;
      margin: 0 0 0.7rem;
      min-height: 11rem;
      position: relative;
      display: flex;
      align-items: flex-end;
      overflow: hidden;
      padding: 1rem;
      color: white;
      background: #777;
      isolation: isolate;
    }
    .gallery-card.portrait {
      height: 19rem;
    }
    .gallery-card.landscape {
      height: 12rem;
    }
    .gallery-card.square {
      height: 14rem;
    }
    .gallery-card::before {
      content: "";
      position: absolute;
      inset: -12%;
      z-index: -1;
      background:
        linear-gradient(135deg, rgba(255, 255, 255, 0.4), transparent 42%),
        var(--card-color);
      transform: rotate(-5deg) scale(1.15);
    }
    .gallery-card::after {
      content: "PLACEHOLDER";
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      letter-spacing: 0.16em;
      font-size: 0.53rem;
      font-weight: bold;
      color: rgba(255, 255, 255, 0.7);
    }
    .wine {
      --card-color: linear-gradient(135deg, #39151e, #a84660);
    }
    .sand {
      --card-color: linear-gradient(135deg, #8f7260, #342827);
    }
    .smoke {
      --card-color: linear-gradient(135deg, #24272a, #798188);
    }
    .olive {
      --card-color: linear-gradient(135deg, #22302b, #889267);
    }
    .gallery-label {
      position: relative;
      z-index: 1;
      text-transform: uppercase;
      font-size: 0.6rem;
      font-weight: bold;
      letter-spacing: 0.1em;
    }
    .gallery-label span {
      display: block;
      opacity: 0.75;
      font-weight: normal;
      margin-top: 0.25rem;
    }
    .gallery-card.has-image::before,
    .gallery-card.has-image::after {
      display: none;
    }
    .gallery-card :is(img, video) {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      z-index: 0;
    }
    .gallery-card.has-image {
      background: #272222;
    }
    .gallery-card.has-image .gallery-label {
      text-shadow: 0 1px 8px rgba(0, 0, 0, 0.6);
    }
    .content-notice {
      margin: -1rem 0 1.5rem;
      padding: 0.85rem 1rem;
      border-left: 2px solid var(--wine);
      max-width: 38rem;
      color: #5a5050;
      font-size: 0.78rem;
      line-height: 1.5;
    }
    .suggestive-overlay {
      position: absolute;
      inset: 0;
      z-index: 1;
      pointer-events: none;
      background: linear-gradient(
        180deg,
        rgba(78, 19, 35, 0.34),
        transparent 42%,
        rgba(0, 0, 0, 0.35)
      );
    }
    .suggestive-badge {
      position: absolute;
      z-index: 2;
      top: 0.75rem;
      right: 0.75rem;
      padding: 0.38rem 0.55rem;
      background: var(--wine);
      color: var(--paper);
      font-size: 0.5rem;
      font-weight: bold;
      letter-spacing: 0.13em;
      text-transform: uppercase;
    }
    .gallery-card.suggestive .gallery-label {
      z-index: 2;
    }
    .gallery-card.restricted :is(img, video) {
      filter: blur(18px);
      transform: scale(1.08);
      transition:
        filter 0.3s ease,
        transform 0.3s ease;
    }
    .gallery-card.restricted.is-revealed :is(img, video) {
      filter: blur(2px);
      transform: scale(1.01);
    }
    .restricted-overlay {
      position: absolute;
      inset: 0;
      z-index: 2;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 0.65rem;
      padding: 1rem;
      text-align: center;
      background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.66),
        rgba(0, 0, 0, 0.22)
      );
      transition: background 0.3s ease;
    }
    .gallery-card.restricted.is-revealed .restricted-overlay {
      background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.22),
        rgba(0, 0, 0, 0.02)
      );
    }
    .restricted-overlay strong {
      font: 400 1.5rem/1 Georgia;
    }
    .restricted-overlay p {
      margin: 0;
      font-size: 0.68rem;
      line-height: 1.4;
    }
    .preview-toggle {
      appearance: none;
      border: 1px solid rgba(245, 243, 239, 0.75);
      color: var(--paper);
      background: rgba(17, 17, 17, 0.45);
      padding: 0.55rem 0.8rem;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      font-size: 0.56rem;
      font-weight: bold;
      cursor: pointer;
    }
    .restricted-link {
      color: var(--paper);
      font-size: 0.6rem;
      font-weight: bold;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      text-underline-offset: 0.3rem;
    }
    .conversion {
      background: #171516;
    }
    .conversion-grid {
      display: grid;
      gap: 1rem;
    }
    .feature {
      padding: 2rem;
      min-height: 23rem;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-end;
      position: relative;
      overflow: hidden;
      background: linear-gradient(140deg, rgba(143, 41, 66, 0.8), #25171b 65%);
    }
    .feature.telegram {
      background: linear-gradient(140deg, #3b4c49, #141a19 70%);
    }
    .feature::before {
      content: "✦";
      position: absolute;
      top: 1.2rem;
      right: 1.5rem;
      font: 5rem Georgia;
      opacity: 0.2;
    }
    .feature h3 {
      font: 400 clamp(2.2rem, 10vw, 4rem)/0.95 Georgia;
      margin: 0;
    }
    .feature p {
      max-width: 23rem;
      color: #e4dad5;
      line-height: 1.6;
    }
    .feature .button {
      margin-top: 1rem;
      width: 100%;
    }
    .about {
      background: #ded7d0;
      color: #191617;
    }
    .about-grid {
      display: grid;
      gap: 2rem;
    }
    .about-photo {
      min-height: 26rem;
      background: linear-gradient(160deg, #2d2422, #9c7565);
      position: relative;
      overflow: hidden;
    }
    .about-photo::after {
      content: "FOTO DE DANIEL";
      color: rgba(255, 255, 255, 0.75);
      font-size: 0.62rem;
      letter-spacing: 0.14em;
      font-weight: bold;
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
    }
    .about-photo.has-image::after {
      display: none;
    }
    .about-photo img {
      width: 100%;
      height: 100%;
      position: absolute;
      inset: 0;
      object-fit: cover;
    }
    .about-copy {
      align-self: center;
    }
    .about-copy p {
      font: 1.2rem/1.55 Georgia;
      max-width: 32rem;
    }
    .location {
      letter-spacing: 0.12em;
      text-transform: uppercase;
      font-size: 0.65rem;
      font-weight: bold;
    }
    .social {
      position: relative;
      z-index: 1;
      isolation: isolate;
      background: #111;
    }
    .link-list {
      border-top: 1px solid var(--line);
    }
    .link-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      text-decoration: none;
      padding: 1.35rem 0;
      border-bottom: 1px solid var(--line);
      transition: padding 0.2s;
    }
    .link-row:hover {
      padding-left: 0.5rem;
    }
    .link-row strong {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font: 400 1.7rem Georgia;
    }
    .social-icon {
      width: 1.6rem;
      height: 1.6rem;
      flex: 0 0 auto;
      object-fit: contain;
    }
    .arrow {
      color: var(--wine-light);
      font-size: 1.3rem;
    }
    .contact {
      background: var(--wine);
      text-align: center;
      padding-block: 6rem;
    }
    .contact .lead {
      color: #f3dce2;
      margin-inline: auto;
    }
    .contact .button {
      width: min(100%, 23rem);
      margin: 2rem auto 0;
      background: #111;
      border-color: #111;
    }
    footer {
      padding: 2.2rem 1.25rem calc(5.5rem + env(safe-area-inset-bottom));
      background: #111;
      color: var(--muted);
      font-size: 0.68rem;
      display: flex;
      flex-direction: column;
      gap: 0.8rem;
    }
    footer a {
      color: var(--paper);
    }
    .mobile-cta {
      position: fixed;
      z-index: 10;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 0.65rem 1rem calc(0.65rem + env(safe-area-inset-bottom));
      background: rgba(17, 17, 17, 0.92);
      backdrop-filter: blur(12px);
    }
    .mobile-cta .button {
      min-height: 3rem;
    }
    .config-warning {
      position: fixed;
      z-index: 20;
      left: 1rem;
      right: 1rem;
      bottom: 5.2rem;
      padding: 0.85rem 1rem;
      border: 1px solid #d79a58;
      background: #31251c;
      color: #ffe5c7;
      font-size: 0.75rem;
      line-height: 1.45;
    }
    .consent {
      position: fixed;
      z-index: 30;
      left: 1rem;
      right: 1rem;
      bottom: 5.2rem;
      background: #f5f3ef;
      color: #151313;
      padding: 1.2rem;
      box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.45);
    }
    .consent p {
      margin: 0.2rem 0 1rem;
      font-size: 0.8rem;
      line-height: 1.5;
    }
    .consent-actions {
      display: flex;
      gap: 0.6rem;
    }
    .consent button {
      flex: 1;
      min-height: 2.7rem;
      border: 1px solid #151313;
      background: transparent;
      font-size: 0.67rem;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      font-weight: bold;
      cursor: pointer;
    }
    .consent button.accept {
      background: #151313;
      color: #f5f3ef;
    }
    .birthday-overlay {
      position: fixed;
      z-index: 50;
      inset: 0;
      display: grid;
      place-items: center;
      padding: 1.25rem;
      background: rgba(10, 7, 8, 0.82);
      backdrop-filter: blur(10px);
    }
    .birthday-card {
      position: relative;
      width: min(100%, 32rem);
      overflow: hidden;
      padding: 2.75rem 1.5rem 1.5rem;
      border: 1px solid rgba(245, 243, 239, 0.32);
      background:
        radial-gradient(circle at 80% 8%, rgba(179, 75, 101, 0.5), transparent 33%),
        linear-gradient(145deg, #2b1118, #111);
      box-shadow: 0 1.5rem 5rem rgba(0, 0, 0, 0.6);
      text-align: center;
      animation: birthday-card-in 420ms ease-out both;
    }
    .birthday-card > *:not(.birthday-confetti) {
      position: relative;
      z-index: 1;
    }
    .birthday-card .eyebrow {
      color: #e4b8c4;
    }
    .birthday-card h2 {
      margin: 0;
      font: 400 clamp(2.6rem, 11vw, 4.4rem) / 0.92 Georgia, "Times New Roman", serif;
      letter-spacing: -0.055em;
    }
    .birthday-card p:not(.eyebrow) {
      max-width: 24rem;
      margin: 1.2rem auto 1.7rem;
      color: #eadfe2;
      line-height: 1.55;
    }
    .birthday-close {
      min-height: 3.1rem;
      width: min(100%, 19rem);
      border: 1px solid var(--paper);
      background: var(--paper);
      color: #251118;
      font: 700 0.68rem/1 Arial, Helvetica, sans-serif;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      cursor: pointer;
    }
    .birthday-confetti {
      position: absolute;
      inset: 0;
      overflow: hidden;
      pointer-events: none;
    }
    .birthday-confetti span {
      position: absolute;
      top: -12%;
      width: 0.45rem;
      height: 0.85rem;
      background: #eecb71;
      animation: birthday-confetti-fall 1500ms cubic-bezier(0.2, 0.75, 0.3, 1) both;
    }
    .birthday-confetti span:nth-child(2n) { background: #d86784; }
    .birthday-confetti span:nth-child(3n) { background: #f5f3ef; }
    .birthday-confetti span:nth-child(1) { left: 8%; animation-delay: 30ms; }
    .birthday-confetti span:nth-child(2) { left: 18%; animation-delay: 220ms; }
    .birthday-confetti span:nth-child(3) { left: 29%; animation-delay: 90ms; }
    .birthday-confetti span:nth-child(4) { left: 42%; animation-delay: 320ms; }
    .birthday-confetti span:nth-child(5) { left: 56%; animation-delay: 150ms; }
    .birthday-confetti span:nth-child(6) { left: 69%; animation-delay: 400ms; }
    .birthday-confetti span:nth-child(7) { left: 81%; animation-delay: 55ms; }
    .birthday-confetti span:nth-child(8) { left: 91%; animation-delay: 270ms; }
    @keyframes birthday-card-in {
      from { opacity: 0; transform: translateY(1rem) scale(0.97); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes birthday-confetti-fall {
      from { opacity: 0; transform: translate3d(0, -1rem, 0) rotate(0deg); }
      15% { opacity: 1; }
      to { opacity: 0; transform: translate3d(2.5rem, 32rem, 0) rotate(300deg); }
    }
    @media (min-width: 700px) {
      .hero {
        padding: 2.5rem 7vw;
        align-items: center;
      }
      .topmark {
        top: 2.5rem;
        left: 7vw;
      }
      .hero-art {
        left: 38%;
        background-position: center;
      }
      .hero::after {
        background: linear-gradient(90deg, rgba(0, 0, 0, 0.5), transparent 65%);
      }
      .actions {
        grid-template-columns: 1fr 1fr;
        max-width: 33rem;
      }
      section {
        padding: 8rem 7vw;
      }
      .masonry {
        columns: 4;
        gap: 1rem;
      }
      .gallery-card {
        margin-bottom: 1rem;
      }
      .gallery-card.portrait {
        height: 29rem;
      }
      .gallery-card.landscape {
        height: 17rem;
      }
      .gallery-card.square {
        height: 20rem;
      }
      .conversion-grid {
        grid-template-columns: 1fr 1fr;
      }
      .feature {
        min-height: 30rem;
        padding: 3rem;
      }
      .about-grid {
        grid-template-columns: 1fr 1fr;
        gap: 6vw;
      }
      .about-photo {
        min-height: 36rem;
      }
      footer {
        padding: 2.2rem 7vw;
        flex-direction: row;
        justify-content: space-between;
      }
      .mobile-cta {
        display: none;
      }
      .config-warning,
      .consent {
        max-width: 31rem;
        left: auto;
        right: 1.5rem;
        bottom: 1.5rem;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      *,
      *::before,
      *::after {
        scroll-behavior: auto !important;
        transition: none !important;
        animation: none !important;
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.consent = localStorage.getItem(CONSENT_KEY);
    if (this.consent === "accepted") void enableAnalytics();
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("keydown", this.onKeydown);
    void import('./firebase-public').then(({ subscribePublishedGallery }) => {
      this.unsubscribeGallery = subscribePublishedGallery((items) => {
        if (items.length) this.gallery = items;
      });
    });
  }
  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("keydown", this.onKeydown);
    this.observer?.disconnect();
    this.unsubscribeGallery?.();
    super.disconnectedCallback();
  }
  firstUpdated() {
    this.applySeo();
    if (this.birthdayOpen)
      this.renderRoot.querySelector<HTMLButtonElement>(".birthday-close")?.focus();
    const gallery = this.renderRoot.querySelector("#gallery");
    if (!gallery) return;
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !this.gallerySeen) {
          this.gallerySeen = true;
          track("gallery_view", { location: "gallery" });
        }
      },
      { threshold: 0.2 },
    );
    this.observer.observe(gallery);
  }
  private onScroll = () => {
    const progress =
      (window.scrollY + window.innerHeight) /
      document.documentElement.scrollHeight;
    [50, 90].forEach((milestone) => {
      if (
        progress >= milestone / 100 &&
        !this.scrollMilestones.has(milestone)
      ) {
        this.scrollMilestones.add(milestone);
        track(`scroll_${milestone}` as AnalyticsEvent, { percentage: milestone });
      }
    });
  };
  private onKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && this.birthdayOpen) this.birthdayOpen = false;
  };
  private applySeo() {
    const copy = localizedCopy[this.locale];
    document.documentElement.lang = this.locale;
    document.title = copy.title;
    const upsertMeta = (selector: string, attribute: "name" | "property", key: string, value: string) => {
      const element = document.querySelector<HTMLMetaElement>(selector) ?? document.createElement("meta");
      element.setAttribute(attribute, key);
      element.content = value;
      if (!element.parentElement) document.head.append(element);
    };
    upsertMeta('meta[name="description"]', "name", "description", copy.description);
    upsertMeta('meta[property="og:description"]', "property", "og:description", copy.socialDescription);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", copy.socialDescription);
    if (!siteConfig.siteUrl) return;
    const baseUrl = siteConfig.siteUrl.replace(/\/$/, "");
    const currentUrl = `${baseUrl}${this.locale === "en" ? "/en/" : "/"}`;
    const canonical =
      document.querySelector<HTMLLinkElement>('link[rel="canonical"]') ??
      document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = currentUrl;
    if (!canonical.parentElement) document.head.append(canonical);
    (["es", "en"] as const).forEach((locale) => {
      const alternate = document.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${locale}"]`) ?? document.createElement("link");
      alternate.rel = "alternate";
      alternate.hreflang = locale;
      alternate.href = `${baseUrl}${locale === "en" ? "/en/" : "/"}`;
      if (!alternate.parentElement) document.head.append(alternate);
    });
    const jsonLd = document.createElement("script");
    jsonLd.type = "application/ld+json";
    jsonLd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Person",
      name: siteConfig.name,
      jobTitle: "Model & Content Creator",
      url: currentUrl,
      sameAs: [
        siteConfig.instagramUrl,
        siteConfig.xUrl,
        siteConfig.facebookUrl,
        siteConfig.threadsUrl,
        siteConfig.telegramFreeUrl,
      ].filter(Boolean),
    });
    document.head.append(jsonLd);
  }
  private saveConsent(choice: "accepted" | "rejected") {
    localStorage.setItem(CONSENT_KEY, choice);
    this.consent = choice;
    if (choice === "accepted") void enableAnalytics();
  }
  private link(link: LinkItem, variant = "") {
    if (!link.href)
      return html`<span class="button ${variant} disabled" aria-disabled="true"
        >${link.iconSrc
          ? html`<img class="button-icon" src=${link.iconSrc} alt="" />`
          : nothing}${link.label}</span
      >`;
    return html`<a
      class="button ${variant}"
      href=${link.href}
      target=${link.href.startsWith("mailto:") ? nothing : "_blank"}
      rel="noopener noreferrer"
      @click=${() => track(link.event, link.analytics)}
      >${link.iconSrc
        ? html`<img class="button-icon" src=${link.iconSrc} alt="" />`
        : nothing}${link.label}</a
    >`;
  }
  private titleWithBreaks(value: string) {
    const [first, second] = value.split("<br />");
    return second ? html`${first}<br />${second}` : value;
  }
  private galleryLabel(item: (typeof galleryItems)[number]) {
    if (this.locale !== "en") return item.label;
    return item.labelEn ?? item.label.replace(/^Galería\s+/i, "Gallery ");
  }
  private galleryCard(item: (typeof galleryItems)[number]) {
    const copy = localizedCopy[this.locale];
    const isRestricted = item.rating === "restricted";
    const imageSrc = isRestricted ? item.previewSrc : item.src;
    const isRevealed = this.revealedRestrictedId === item.id;
    const exclusiveHref = item.exclusiveUrl ?? siteConfig.exclusiveUrl;
    const isVideo = Boolean(
      imageSrc && /\.(mp4|mov|webm)(?:\?.*)?$/i.test(imageSrc),
    );
    return html`<article
      class="gallery-card ${item.ratio} ${item.tone} ${imageSrc
        ? "has-image"
        : ""} ${item.rating} ${isRevealed ? "is-revealed" : ""}"
      aria-label=${imageSrc
        ? this.locale === "en" ? item.altEn ?? copy.photoFallback : item.alt
        : copy.placeholder.replace("{label}", item.label)}
    >
      ${imageSrc
        ? isVideo
          ? html`<video
              src=${imageSrc}
              aria-label=${this.locale === "en" ? item.altEn ?? copy.photoFallback : item.alt}
              muted
              loop
              playsinline
              preload="metadata"
              ?autoplay=${isRevealed}
              @play=${() => this.trackUserInitiatedVideoPlay(item.id)}
            ></video>`
          : html`<img
              src=${imageSrc}
              alt=${this.locale === "en" ? item.altEn ?? copy.photoFallback : item.alt}
              loading="lazy"
              decoding="async"
            />`
        : nothing}
      ${item.rating === "suggestive"
        ? html`<div class="suggestive-overlay" aria-hidden="true"></div>
            <span class="suggestive-badge">Preview</span>`
        : nothing}
      ${isRestricted
        ? html`<div class="restricted-overlay">
            <strong>${copy.restrictedPreview}</strong>
            <p>${copy.subscriberOnly}</p>
            <button
              class="preview-toggle"
              aria-pressed=${String(isRevealed)}
              @click=${() => {
                const buttonText = isRevealed ? copy.hidePreview : copy.showPreview;
                track("gallery_click", {
                  location: "gallery",
                  destination: "preview",
                  button_text: buttonText,
                  content_id: item.id,
                });
                if (!isRevealed && isVideo) this.userInitiatedVideoIds.add(item.id);
                this.revealedRestrictedId = isRevealed ? undefined : item.id;
              }}
            >
              ${isRevealed ? copy.hidePreview : copy.showPreview}</button
            >${exclusiveHref
              ? html`<a
                  class="restricted-link"
                  href=${exclusiveHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  @click=${() =>
                    track("exclusive_click", {
                      location: "gallery",
                      destination: "subscription",
                      button_text: copy.exclusive,
                      content_id: item.id,
                    })}
                  >${copy.exclusive}</a
                >`
              : nothing}
          </div>`
        : nothing}
      <div class="gallery-label">
        ${item.category}<span>${this.galleryLabel(item)}</span>
      </div>
    </article>`;
  }
  private trackUserInitiatedVideoPlay(contentId: string) {
    if (
      !this.userInitiatedVideoIds.has(contentId) ||
      this.playedVideoIds.has(contentId)
    )
      return;
    this.playedVideoIds.add(contentId);
    track("video_play", {
      location: "gallery",
      destination: "video",
      content_id: contentId,
    });
  }
  render() {
    const copy = localizedCopy[this.locale];
    const exclusive = (
      location: "hero" | "exclusive_section" | "sticky_bottom",
    ): LinkItem => ({
      label: copy.exclusive,
      iconSrc: "/logos/onlyFans-logo.svg",
      href: siteConfig.exclusiveUrl,
      event: "exclusive_click",
      analytics: {
        location,
        destination: "subscription",
        button_text: copy.exclusive,
      },
    });
    const telegram = (location: "hero" | "private_group"): LinkItem => ({
      label: copy.privateGroup,
      iconSrc: "/logos/telegram-logo.svg",
      href: siteConfig.telegramUrl(this.locale),
      event: "telegram_click",
      analytics: {
        location,
        destination: "telegram",
        button_text: copy.privateGroup,
      },
    });
    return html`
      <main class="page">
        <section class="hero" aria-labelledby="site-title">
          <div class="hero-art" aria-hidden="true"></div>
          <div class="topmark">DM<span>Medellín · Colombia</span></div>
          <nav class="language-switcher" aria-label=${copy.languageLabel}>
            <a href="/" aria-current=${this.locale === "es" ? "page" : nothing} @click=${() => track("language_switch", { location: "hero", destination: "language", button_text: "ES" })}>ES</a>
            <span class="language-separator" aria-hidden="true">·</span>
            <a href="/en/" aria-current=${this.locale === "en" ? "page" : nothing} @click=${() => track("language_switch", { location: "hero", destination: "language", button_text: "EN" })}>EN</a>
          </nav>
          <div class="hero-content">
            <p class="eyebrow">${copy.officialSite}</p>
            <h1 id="site-title" class="display">Daniel<br />Montana</h1>
            <p class="descriptor">${siteConfig.descriptor}</p>
            <p class="intro">
              ${copy.intro}
            </p>
            <div class="actions">
              ${this.link(exclusive("hero"), "primary")}${this.link(
                telegram("hero"),
                "secondary",
              )}
            </div>
          </div>
        </section>
        <section class="gallery" id="gallery" aria-labelledby="gallery-title">
          <div class="section-heading">
            <p class="eyebrow">${copy.selectedWork}</p>
            <h2 id="gallery-title" class="display">
              ${this.titleWithBreaks(copy.galleryTitle)}
            </h2>
            <p class="lead">
              ${copy.galleryLead}
            </p>
          </div>
          ${this.gallery.some((item) => item.rating === "restricted")
            ? html`<p class="content-notice">
                ${copy.contentNotice}
              </p>`
            : nothing}
          <div class="masonry">
            ${this.gallery.map((item) => this.galleryCard(item))}
          </div>
        </section>
        <section class="conversion" aria-label=${copy.exclusiveAccess}>
          <div class="conversion-grid">
            <article class="feature">
              <p class="eyebrow">${copy.moreFromDaniel}</p>
              <h3>${this.titleWithBreaks(copy.exclusiveTitle)}</h3>
              <p>${copy.exclusiveDescription}</p>
              ${this.link(exclusive("exclusive_section"), "primary")}
            </article>
            <article class="feature telegram">
              <p class="eyebrow">${copy.privateAccess}</p>
              <h3>${this.titleWithBreaks(copy.privateTitle)}</h3>
              <p>${copy.privateDescription}</p>
              ${this.link(telegram("private_group"), "secondary")}
            </article>
          </div>
        </section>
        <section class="about" aria-labelledby="about-title">
          <div class="about-grid">
            <div
              class="about-photo ${aboutPhoto.src ? "has-image" : ""}"
              role="img"
              aria-label=${aboutPhoto.src
                ? this.locale === "en" ? copy.photoFallback : aboutPhoto.alt
                : copy.aboutFallback}
            >
              ${aboutPhoto.src
                ? html`<img
                    src=${aboutPhoto.src}
                    alt=${this.locale === "en" ? copy.photoFallback : aboutPhoto.alt}
                    loading="lazy"
                    decoding="async"
                  />`
                : nothing}
            </div>
            <div class="about-copy">
              <p class="eyebrow">About</p>
              <h2 id="about-title" class="display">${this.titleWithBreaks(copy.aboutTitle)}</h2>
              <p>
                ${copy.aboutDescription}
              </p>
              <p class="location">Medellín · Colombia</p>
            </div>
          </div>
        </section>
        <section class="social" aria-labelledby="social-title">
          <p class="eyebrow">${copy.follow}</p>
          <h2 id="social-title" class="display">
            ${this.titleWithBreaks(copy.socialTitle)}
          </h2>
          <div class="link-list">
            ${socialLinks(this.locale).map((link) =>
              link.href
                ? html`<a
                    class="link-row"
                    href=${link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    @click=${() => track(link.event, link.analytics)}
                    ><span><strong
                        >${link.iconSrc
                          ? html`<img
                              class="social-icon"
                              src=${link.iconSrc}
                              alt=""
                            />`
                          : nothing}${link.label}</strong
                      ></span
                    ><span class="arrow" aria-hidden="true">↗</span></a
                  >`
                : html`<div class="link-row" aria-disabled="true">
                    <span><strong
                        >${link.iconSrc
                          ? html`<img
                              class="social-icon"
                              src=${link.iconSrc}
                              alt=""
                            />`
                          : nothing}${link.label}</strong
                      ></span
                    >
                  </div>`,
            )}
          </div>
        </section>
        <section class="contact" aria-labelledby="contact-title">
          <p class="eyebrow">${copy.collaborations}</p>
          <h2 id="contact-title" class="display">${this.titleWithBreaks(copy.contactTitle)}</h2>
          <p class="lead">
            ${copy.contactLead}
          </p>
          ${this.link(contactLink(this.locale), "secondary")}
        </section>
        <footer>
          <span>© ${new Date().getFullYear()} Daniel Montana</span
          ><a
            href=${this.locale === "en" ? "/en/privacy.html" : "/privacy.html"}
            @click=${() =>
              track("footer_click", {
                location: "footer",
                destination: "privacy",
                button_text: copy.privacy,
              })}
            >${copy.privacy}</a
          ><span>${copy.footer}</span>
        </footer>
      </main>
      <div class="mobile-cta">${this.link(exclusive("sticky_bottom"), "primary")}</div>
      ${this.birthdayOpen
        ? html`<aside class="birthday-overlay">
            <section class="birthday-card" role="dialog" aria-modal="true" aria-labelledby="birthday-title" aria-describedby="birthday-message">
              <div class="birthday-confetti" aria-hidden="true">
                ${Array.from({ length: 8 }, () => html`<span></span>`)}
              </div>
              <p class="eyebrow">${copy.birthdayDate}</p>
              <h2 id="birthday-title">${copy.birthdayTitle}</h2>
              <p id="birthday-message">${copy.birthdayMessage}</p>
              <button class="birthday-close" type="button" @click=${() => (this.birthdayOpen = false)}>
                ${copy.birthdayClose}
              </button>
            </section>
          </aside>`
        : nothing}
      ${missingRequiredLinks.length
        ? html`<aside class="config-warning" role="status">
            <strong>${copy.pendingConfig}</strong>
            ${copy.pendingConfigBody.replace("{keys}", missingRequiredLinks.join(", "))}
          </aside>`
        : nothing}
      ${this.consent === null
        ? html`<aside class="consent" aria-label=${copy.privacyPreferences}>
            <strong>${copy.yourPrivacy}</strong>
            <p>
              ${copy.privacyMessage}
            </p>
            <div class="consent-actions">
              <button @click=${() => this.saveConsent("rejected")}>
                ${copy.reject}</button
              ><button
                class="accept"
                @click=${() => this.saveConsent("accepted")}
              >
                ${copy.accept}
              </button>
            </div>
          </aside>`
        : nothing}
    `;
  }
}
